// Ch78 · WHERE LEADS DIE — the funnel, as six bars and one sentence.
//
// Six counts over one cohort, each a strict subset of the one above, so the
// percentages compose and a short bar is a real leak. A step whose ledger could
// not be read draws no bar and says UNKNOWN with its reason — it never draws a
// zero, which on a funnel reads as "everybody dropped out here".
//
// `converted` sits in the middle because the schema puts it there: the SMS
// thread is keyed by client_id, so a lead cannot be texted until conversion
// mints their client row. That is the single most useful thing this card says,
// and it is in the (i) rather than in a paragraph on the page.

import React from "react";
import { Box, Paper, Skeleton, Stack, Tooltip, Typography, alpha, useTheme } from "@mui/material";
import WorkObligationSection from "./WorkObligationSection";
import CommercialScorecardSection from "./CommercialScorecardSection";
import InfoDot from "./InfoDot";
import { describeLeak, type LeadFunnel } from "../../services/leadFunnelService";

export interface LeadFunnelCardProps {
  funnel: LeadFunnel | null;
  loading: boolean;
  /** The read failed outright — say so instead of drawing an empty funnel. */
  error: string | null;
  windowDays: number;
}

const FUNNEL_INFO =
  "Where leads die. One cohort — the real leads that ARRIVED in this window — followed forwards, " +
  "so each step is a subset of the one above it and every percentage is “of the step it names” — `Engaged` is measured against `Converted`, not against `Replied`, because somebody can tour after a phone call without ever writing back. " +
  "Converted sits in the middle because the SMS thread is keyed by client_id: a lead cannot be " +
  "texted until conversion creates their client row, so “claimed but never contacted” is a " +
  "conversion problem, not a diligence problem. A step whose ledger cannot be read says UNKNOWN " +
  "and why — never zero.";

export default function LeadFunnelCard({ funnel, loading, error, windowDays }: LeadFunnelCardProps) {
  const theme = useTheme();
  // Belt and braces beside the service's shape check: a card that can be
  // handed a prop must not be the thing that crashes when it is a bad one.
  const steps = funnel?.steps ?? [];
  const top = steps[0]?.count ?? null;

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }} data-testid="leads-funnel">
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={800}>Funnel · last {windowDays} days</Typography>
        <InfoDot text={FUNNEL_INFO} testId="funnel-info" />
      </Stack>

      {loading ? (
        <Stack spacing={1} data-testid="funnel-loading">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rounded" height={22} />
          ))}
        </Stack>
      ) : error ? (
        <Typography variant="body2" color="warning.main" data-testid="funnel-error">
          The funnel could not be read: {error}
        </Typography>
      ) : steps.length === 0 ? null : (
        <>
          <Stack spacing={1} data-testid="funnel-steps">
            {steps.map((step) => {
              const against = steps.find((s) => s.key === step.measured_against);
              const known = step.count !== null;
              const width = known && top ? Math.max(2, (100 * (step.count as number)) / top) : 0;
              return (
                <Box
                  key={step.key}
                  data-testid={`funnel-step-${step.key}`}
                  sx={{ display: "grid", gridTemplateColumns: "84px 1fr 44px 46px", gap: 1, alignItems: "center" }}
                >
                  <Typography variant="caption" noWrap>{step.label}</Typography>
                  <Tooltip arrow title={known ? step.definition_id : step.unknown_reason ?? "UNKNOWN"}>
                    <Box sx={{ height: 20, borderRadius: 1, bgcolor: alpha(theme.palette.text.primary, 0.08), overflow: "hidden" }}>
                      {known && (
                        <Box sx={{ width: `${width}%`, height: "100%", bgcolor: "primary.main", opacity: 0.85 }} />
                      )}
                    </Box>
                  </Tooltip>
                  <Typography variant="caption" sx={{ fontWeight: 700, textAlign: "right" }}>
                    {known ? step.count : "UNKNOWN"}
                  </Typography>
                  <Tooltip
                    arrow
                    title={
                      step.of_previous_pct === null || !against
                        ? ""
                        : `${step.count} of the ${against.count} ${against.label.toLowerCase()}`
                    }
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: "right" }}>
                      {step.of_previous_pct === null ? "" : `${step.of_previous_pct}%`}
                    </Typography>
                  </Tooltip>
                </Box>
              );
            })}
          </Stack>

          <Typography
            variant="caption"
            sx={{ display: "block", mt: 1.5, color: funnel?.biggest_leak ? "warning.main" : "text.secondary" }}
            data-testid="funnel-leak"
          >
            {describeLeak(funnel as LeadFunnel)}
          </Typography>

          {funnel && funnel.unlinked_conversions !== null && funnel.unlinked_conversions > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }} data-testid="funnel-unlinked">
              {funnel.unlinked_conversions} conversion{funnel.unlinked_conversions === 1 ? "" : "s"} in this
              window have no claim row, so they are counted here and not inside the funnel.
            </Typography>
          )}
        </>
      )}
      {!loading && !error && funnel && <CommercialScorecardSection scorecard={funnel.commercial_scorecard} />}
      {!loading && !error && funnel && <WorkObligationSection preview={funnel.work_obligations} />}
    </Paper>
  );
}
